import React from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { Draggable } from '@hello-pangea/dnd';
import { IssuePriorityIcon2 } from 'components';
import { IssueState, IssueStatus } from 'shared/constants/issues';
import { Chip } from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  IssueLink,
  Issue,
  KanbanItemTitle,
  Bottom,
  Assignees,
  AssigneeAvatar,
  IssueTitle,
  IssueProperty,
  IssueTags,
} from './Styles';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import { UserProptypes } from 'shared/propTypes/userType';
import { IssuePropTypes } from 'shared/propTypes/issueType';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import PersonIcon from '@mui/icons-material/Person';
import localData from 'utils/localData';
import { USER_DATA } from 'shared/constants/users';
import logger from 'utils/logger';

const log = logger.createLogger('ProjectBoardListIssue');

const propTypes = {
  projectUsers: PropTypes.arrayOf(PropTypes.shape(UserProptypes)).isRequired,
  issue: PropTypes.shape(IssuePropTypes).isRequired,
  index: PropTypes.number.isRequired,
};

const ProjectBoardListIssue = ({ projectUsers, issue, index }) => {
  const { pathname } = useLocation();
  const assignees = issue.userIds.map((userId) =>
    projectUsers.find((user) => user.id === userId)
  );

  // Validate issue ID before navigation
  if (!issue.id) {
    log.error('Issue ID is missing or invalid:', issue);
    return null; // or show an error message
  }

  return (
    <Draggable draggableId={issue.id.toString()} index={index}>
      {(provided, snapshot) => (
        <IssueLink
          to={`${pathname}/issues/${issue.id}`}
          state={{ trigger: 'byClick' }}
          ref={provided.innerRef}
          data-testid="list-issue"
          {...provided.draggableProps}
          {...provided.dragHandleProps}>
          <Issue
            status={issue.status === IssueStatus.DONE}
            color={'blue'}
            state={issue.state}
            isBeingDragged={snapshot.isDragging && !snapshot.isDropAnimating}>
            <KanbanItemTitle>
              <Grid container>
                <Grid size={12}>
                  <IssueTitle>
                    <PersonIcon fontSize="medium" sx={{ margin: '0 3px' }} />
                    <p
                      style={{ fontWeight: 'bold' }}
                      data-testid="kanban-issue-patient-name">
                      {issue.patientName}
                    </p>
                  </IssueTitle>
                </Grid>

                <Grid size={12}>
                  <IssueProperty>
                    <LocationCityIcon
                      fontSize="medium"
                      sx={{ margin: '0 3px' }}
                    />
                    <p>{issue.srcOrgName}</p>
                  </IssueProperty>
                </Grid>

                <Grid size={12}>
                  <IssueProperty>
                    <FingerprintIcon
                      fontSize="medium"
                      sx={{ margin: '0 3px' }}
                    />
                    <p data-testid="kanban-issue-uuid">{issue.title}</p>
                  </IssueProperty>
                </Grid>
                <Grid size={12}>
                  <IssueTags>
                    {issue.tags.map((tag) => {
                      const languageCode = localData.get(
                        USER_DATA.PREFFERED_LANGUAGE
                      );
                      let tagLabelText = tag;
                      if (
                        tag &&
                        Object.prototype.hasOwnProperty.call(tag, 'content')
                      ) {
                        tagLabelText =
                          tag.content.displayName[languageCode] ||
                          tag.content.displayName['en-US'] ||
                          Object.values(tag.content.displayName)[0];
                      } else if (
                        tag &&
                        Object.prototype.hasOwnProperty.call(tag, 'labelText')
                      ) {
                        tagLabelText = tag;
                      }
                      return (
                        <Chip
                          size="small"
                          key={tag.id}
                          label={tagLabelText}
                          style={{
                            margin: '2px 3px',
                            border: '2px solid',
                          }}
                        />
                      );
                    })}
                  </IssueTags>
                </Grid>
              </Grid>
            </KanbanItemTitle>
            <Bottom>
              <div>
                <IssuePriorityIcon2 priority={issue.priority} />
              </div>
              <Assignees>
                {assignees.map((user) => (
                  <AssigneeAvatar
                    key={user.id}
                    size={24}
                    name={user.firstName}
                  />
                ))}
              </Assignees>
            </Bottom>
          </Issue>
        </IssueLink>
      )}
    </Draggable>
  );
};

ProjectBoardListIssue.propTypes = propTypes;

export default ProjectBoardListIssue;
